// ── Interface traductions ─────────────────────────────────────────────────────
//
// Toute la vue du module traductions vit ici : la création du squelette d'une
// ligne (créerLigneSuggestion) et le rendu de son contenu (updateTradLigneHTML).
// Aucune dépendance : les données nécessaires (label) sont passées en paramètre
// par l'orchestrateur (index.js).

/** Produit le label court pour la ligne de suggestion ("en-fr" → "en ⮕ fr : "). */
function labelLigne(clé) {
    const [src, dst] = clé.split('-');
    return src.toLowerCase() + ' ⮕ ' + dst.toLowerCase() + ' : ';
}

/**
 * Affiche ou masque toute la section des traductions (#dsr-traductions),
 * label « Traductions : » compris. Appelé par l'orchestrateur une fois toutes
 * les promesses résolues, selon qu'au moins une paire a produit une traduction.
 *
 * @param {boolean} visible
 */
export function afficherSectionTraductions(visible) {
    const section = document.getElementById('dsr-traductions');
    if (section) section.style.display = visible ? 'block' : 'none';
}

/**
 * Crée et insère un div de ligne de suggestion (squelette vide) pour une paire.
 * Réutilisé par l'initialisation au chargement ET lors de l'ajout d'une paire custom.
 *
 * @param {HTMLElement} conteneur élément parent où insérer la ligne
 * @param {string}      clé       identifiant de paire ("en-fr", "fr-en", …)
 * @param {string}      [label]   libellé long pour l'infobulle (title) ; défaut : la clé
 */
export function créerLigneSuggestion(conteneur, clé, label) {
    const div  = document.createElement('div');
    div.id            = 'dsr-trad-' + clé;
    div.className     = 'dsr-trad-ligne';
    div.style.display = 'none';

    const span = document.createElement('span');
    span.className   = 'dsr-trad-label';
    span.title       = label ?? clé;
    span.textContent = labelLigne(clé);

    const ul = document.createElement('ul');
    ul.id        = 'dsr-trad-' + clé + '-liste';
    ul.className = 'dsr-suggestions-liste';

    div.appendChild(span);
    div.appendChild(ul);
    conteneur.appendChild(div);
}

/**
 * Met à jour la ligne de traduction d'une paire dans le panneau de suggestions.
 *
 * Texte affiché et comportement au clic selon la direction de la paire :
 *   - xx→fr (ex. "en-fr") : affiche le lemme français ; le clic cherche ce lemme
 *   - fr→xx (ex. "fr-en") : affiche la traduction dans la langue cible (dédupliquée
 *                            sur toutes les variantes) ; lorsqu'il subsiste plusieurs
 *                            variantes, chacune est suivie de sa/ses région(s) entre
 *                            parenthèses (ex. "soccer (US, CA) | football (GB)") ;
 *                            le clic cherche le lemme français
 *
 * @param {string} clé    identifiant de paire ("en-fr", "fr-en", …)
 * @param {Array}  lexies tableau de LexieTraduction retourné par l'API
 */
export function updateTradLigneHTML(clé, lexies) {
    const ligneÉlément    = document.getElementById('dsr-trad-' + clé);
    const listeÉlémentOld = document.getElementById('dsr-trad-' + clé + '-liste');
    if (!ligneÉlément || !listeÉlémentOld) return;

    const [, dst] = clé.split('-');
    const versLeFrançais = (dst === 'fr');

    const listeÉlémentNew = document.createElement('ul');
    listeÉlémentNew.id        = listeÉlémentOld.id;
    listeÉlémentNew.className = listeÉlémentOld.className;

    lexies.forEach(function(lexie, i) {
        // Texte affiché : lemme français (xx→fr) ou traduction dédupliquée (fr→xx)
        let textAffiché;
        if (versLeFrançais) {
            textAffiché = lexie.lemme;
        } else {
            // Regroupe les traductions par valeur en mémorisant la/les région(s)
            // (locale) d'où chaque valeur provient. Ex. "trad_lemme_en_US" → "US".
            const régionsParValeur = new Map(); // valeur → [régions]
            Object.entries(lexie.traductions)
                .filter(function([champ]) { return !champ.endsWith('_varie'); })
                .forEach(function([champ, vals]) {
                    const région = champ.split('_').pop(); // "trad_lemme_en_US" → "US"
                    vals.filter(Boolean).forEach(function(val) {
                        if (!régionsParValeur.has(val)) régionsParValeur.set(val, []);
                        const régions = régionsParValeur.get(val);
                        if (!régions.includes(région)) régions.push(région);
                    });
                });

            const valeurs = [...régionsParValeur.keys()];
            if (valeurs.length === 0) {
                // Aucune traduction dans la langue cible pour cette lexie : ne rien
                // afficher (sinon on retomberait à tort sur le lemme français).
                return;
            } else if (valeurs.length === 1) {
                // Pas de variation : un seul mot, sans annotation de région.
                textAffiché = valeurs[0];
            } else {
                // Plusieurs variantes : on annote chacune de sa/ses région(s).
                // Ex. "soccer (US, CA) / football (GB)".
                textAffiché = valeurs
                    .map(function(val) { return val + ' (' + régionsParValeur.get(val).join(', ') + ')'; })
                    .join(' / ');
            }
        }

        const a = document.createElement('a');
        a.href        = '';
        a.tabIndex    = 200 + i;
        a.textContent = textAffiché;
        if (lexie.définition) a.title = lexie.définition;

        a.addEventListener('click', function(e) {
            e.preventDefault();
            // Le clic recherche toujours le lemme français (source de vérité Usito)
            if (typeof window.rechercher === 'function') {
                window.rechercher(lexie.lemme, true);
            }
        });

        const li = document.createElement('li');
        li.className = 'dsr-suggestions-liste-élément';
        li.appendChild(a);
        listeÉlémentNew.appendChild(li);
    });

    ligneÉlément.replaceChild(listeÉlémentNew, listeÉlémentOld);
    // Afficher la ligne uniquement s'il reste au moins un élément rendu : une lexie
    // sans traduction dans la langue cible est filtrée ci-dessus, donc une ligne peut
    // se retrouver vide même si des lexies ont été retournées par l'API.
    ligneÉlément.style.display = listeÉlémentNew.children.length > 0 ? 'block' : 'none';
}
